import { memo, useCallback, useRef } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { db } from '@/db/schema'
import type { CategoryRow, EventTypeRow } from '@/db/types'
import { labelColorOn } from '@/design/contrast'
import { StamperStrip } from '@/features/stampers/StamperStrip'
import type { StamperRow } from '@/db/types'
import './picker.css'

interface EventPickerProps {
  onPick: (eventTypeId: string) => void
  /** Long press: mark, then open the block with the comment field focused. */
  onPickAndComment: (eventTypeId: string) => void
  onErase: () => void
  /** Disables everything when nothing is selected. */
  hasSelection: boolean
  /**
   * Collapse is read from the category row, not from React state.
   *
   * It is a synced field, so a layout arranged on the iPad is the layout the
   * iPhone shows. The picker is the surface this app mostly IS, and having it
   * folded differently on each device is a small, constant irritation.
   */
  onToggleCategory: (category: CategoryRow) => void
  /** Applies a saved type + object + comment combination in one tap. */
  onApplyStamper: (stamper: StamperRow) => void
}

/**
 * The bottom panel: categories of event types, one tap to fill.
 *
 * This and the timeline are the whole product. Every decision here is about
 * making the second tap fast -- chips are large, always visible, and never
 * more than one level deep.
 */
export function EventPicker({
  onPick,
  onPickAndComment,
  onErase,
  hasSelection,
  onToggleCategory,
  onApplyStamper,
}: EventPickerProps) {
  const categories = useLiveQuery(
    () => db.categories.filter((c) => c.deletedAt === null && c.isArchived === 0).sortBy('sortOrder'),
    [],
    [],
  )

  const eventTypes = useLiveQuery(
    () => db.eventTypes.filter((t) => t.deletedAt === null && t.isArchived === 0).sortBy('sortOrder'),
    [],
    [],
  )

  const pinned = eventTypes.filter((t) => t.isPinned === 1)

  const liveCategoryIds = new Set(categories.map((c) => c.id))
  const orphans = eventTypes.filter(
    (t) => t.categoryId === null || !liveCategoryIds.has(t.categoryId),
  )

  return (
    <div className="pk">
      <div className="pk-actions">
        <button
          className="pk-erase"
          onClick={onErase}
          disabled={!hasSelection}
          aria-label="Erase selected blocks"
        >
          Erase
        </button>
        <span className="pk-hint">
          {hasSelection ? 'Tap an event to fill' : 'Tap a block to start'}
        </span>
      </div>

      {/* Above the scroll area, not inside it: a stamper is the fastest thing
          in the picker and should never need scrolling to reach. */}
      <StamperStrip onApply={onApplyStamper} disabled={!hasSelection} />

      <div className="pk-scroll scroll-y">
        {pinned.length > 0 ? (
          <section className="pk-section">
            <ChipRow
              types={pinned}
              onPick={onPick}
              onPickAndComment={onPickAndComment}
              disabled={!hasSelection}
            />
          </section>
        ) : null}

        {categories.map((category) => {
          const types = eventTypes.filter((t) => t.categoryId === category.id)
          if (types.length === 0) return null

          const collapsed = category.isCollapsed === 1

          return (
            <section className="pk-section" key={category.id}>
              <button
                className="pk-cat"
                onClick={() => onToggleCategory(category)}
                aria-expanded={!collapsed}
              >
                <span className="pk-cat-dot" style={{ background: category.colorHex }} aria-hidden="true" />
                <span className="pk-cat-name">{category.name}</span>
                <span className="pk-cat-chevron" aria-hidden="true">
                  {collapsed ? '+' : '−'}
                </span>
              </button>

              {collapsed ? null : (
                <ChipRow
                  types={types}
                  onPick={onPick}
                  onPickAndComment={onPickAndComment}
                  disabled={!hasSelection}
                />
              )}
            </section>
          )
        })}

        {/* Types with no category, normally the survivors of a deleted one.
            Without this they would be invisible in the picker while still
            owning history -- unreachable but not gone, the worst of both. */}
        {orphans.length > 0 ? (
          <section className="pk-section">
            <div className="pk-cat pk-cat--static">
              <span className="pk-cat-dot pk-cat-dot--none" aria-hidden="true" />
              <span className="pk-cat-name">Uncategorised</span>
            </div>
            <ChipRow
              types={orphans}
              onPick={onPick}
              onPickAndComment={onPickAndComment}
              disabled={!hasSelection}
            />
          </section>
        ) : null}
      </div>
    </div>
  )
}

interface ChipRowProps {
  types: EventTypeRow[]
  onPick: (eventTypeId: string) => void
  onPickAndComment: (eventTypeId: string) => void
  disabled: boolean
}

function ChipRow({ types, onPick, onPickAndComment, disabled }: ChipRowProps) {
  return (
    <div className="pk-chips">
      {types.map((type) => (
        <Chip
          key={type.id}
          type={type}
          onPick={onPick}
          onPickAndComment={onPickAndComment}
          disabled={disabled}
        />
      ))}
    </div>
  )
}

interface ChipProps {
  type: EventTypeRow
  onPick: (eventTypeId: string) => void
  onPickAndComment: (eventTypeId: string) => void
  disabled: boolean
}

/**
 * Memoised, and the click handler is created per chip rather than inline in
 * the parent's map, so a re-render of the picker does not invalidate every chip.
 */
const CHIP_LONG_PRESS_MS = 350

const Chip = memo(function Chip({ type, onPick, onPickAndComment, disabled }: ChipProps) {
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const longPressed = useRef(false)

  const startLongPress = useCallback(() => {
    if (disabled) return
    longPressed.current = false
    timer.current = setTimeout(() => {
      longPressed.current = true
      onPickAndComment(type.id)
    }, CHIP_LONG_PRESS_MS)
  }, [disabled, onPickAndComment, type.id])

  const cancelLongPress = useCallback(() => {
    if (timer.current) clearTimeout(timer.current)
  }, [])

  /**
   * The tap stays an ordinary click, deliberately.
   *
   * Handling it on pointerup instead would drop keyboard support entirely:
   * Enter and Space fire `click` and no pointer events at all, so the chips
   * would become unusable with an iPad keyboard or a switch device. The long
   * press only has to suppress the click that follows it.
   */
  const handleClick = useCallback(() => {
    cancelLongPress()
    if (longPressed.current) {
      longPressed.current = false
      return
    }
    onPick(type.id)
  }, [cancelLongPress, onPick, type.id])

  return (
    <button
      className="pk-chip"
      style={{ background: type.colorHex, color: labelColorOn(type.colorHex) }}
      onPointerDown={startLongPress}
      onPointerUp={cancelLongPress}
      onPointerLeave={cancelLongPress}
      onPointerCancel={cancelLongPress}
      onClick={handleClick}
      disabled={disabled}
      title={`${type.name} — hold to add a comment`}
    >
      {type.shortcutText || type.name}
    </button>
  )
})

export type { CategoryRow }
